import Dagre from '@dagrejs/dagre';
import type { Edge, Node } from '@xyflow/react';
import { EDGE_ROUTE_PADDING } from '../K1HierarchyEdge/K1HierarchyEdge.utils';
import type { NodeRect } from './K1HierarchyFlowGraph.types';

export const NODE_W = 270;
export const NODE_H = 185;
export const EDGE_LANE_PADDING = 54;

function rangesOverlap(a1: number, a2: number, b1: number, b2: number) {
  return Math.max(a1, b1) <= Math.min(a2, b2);
}

function isBetween(value: number, min: number, max: number) {
  return value >= min && value <= max;
}

function getNodeRect(node: Node): NodeRect {
  return {
    id: node.id,
    x: node.position.x,
    y: node.position.y,
    width: node.measured?.width ?? NODE_W,
    height: node.measured?.height ?? NODE_H,
  };
}

function getLaneXForEdge(edge: Edge, rects: NodeRect[], rectById: Map<string, NodeRect>) {
  const source = rectById.get(edge.source);
  const target = rectById.get(edge.target);

  if (!source || !target) return null;

  const sourceX = source.x + source.width / 2;
  const targetX = target.x + target.width / 2;
  const sourceBottom = source.y + source.height;
  const targetTop = target.y;

  if (targetTop <= sourceBottom) return null;

  const midY = (sourceBottom + targetTop) / 2;
  const minX = Math.min(sourceX, targetX);
  const maxX = Math.max(sourceX, targetX);
  const blockers = rects.filter((rect) => {
    if (rect.id === source.id || rect.id === target.id) return false;

    const expandedLeft = rect.x - EDGE_ROUTE_PADDING;
    const expandedRight = rect.x + rect.width + EDGE_ROUTE_PADDING;
    const expandedTop = rect.y - EDGE_ROUTE_PADDING;
    const expandedBottom = rect.y + rect.height + EDGE_ROUTE_PADDING;
    const verticalOverlap = rangesOverlap(sourceBottom, targetTop, expandedTop, expandedBottom);
    const verticalLegHits = isBetween(sourceX, expandedLeft, expandedRight) || isBetween(targetX, expandedLeft, expandedRight);
    const middleLegHits = isBetween(midY, expandedTop, expandedBottom) && rangesOverlap(minX, maxX, expandedLeft, expandedRight);

    return verticalOverlap && (verticalLegHits || middleLegHits);
  });

  if (!blockers.length) return null;

  const leftLane = Math.min(...blockers.map((rect) => rect.x)) - EDGE_LANE_PADDING;
  const rightLane = Math.max(...blockers.map((rect) => rect.x + rect.width)) + EDGE_LANE_PADDING;
  const scoreLane = (laneX: number) =>
    rects.reduce((score, rect) => {
      if (rect.id === source.id || rect.id === target.id) return score;

      const expandedLeft = rect.x - EDGE_ROUTE_PADDING;
      const expandedRight = rect.x + rect.width + EDGE_ROUTE_PADDING;
      const expandedTop = rect.y - EDGE_ROUTE_PADDING;
      const expandedBottom = rect.y + rect.height + EDGE_ROUTE_PADDING;

      return isBetween(laneX, expandedLeft, expandedRight) && rangesOverlap(sourceBottom, targetTop, expandedTop, expandedBottom)
        ? score + 1
        : score;
    }, 0);

  const leftScore = scoreLane(leftLane);
  const rightScore = scoreLane(rightLane);

  if (leftScore !== rightScore) return leftScore < rightScore ? leftLane : rightLane;
  return targetX <= sourceX ? leftLane : rightLane;
}

function routeEdgesAroundNodes(nodes: Node[], edges: Edge[]) {
  const rects = nodes.map(getNodeRect);
  const rectById = new Map(rects.map((rect) => [rect.id, rect]));

  return edges.map((edge) => {
    const laneX = getLaneXForEdge(edge, rects, rectById);
    if (laneX == null) return edge;

    return {
      ...edge,
      type: 'k1RoutedEdge',
      data: { ...(edge.data ?? {}), laneX },
      zIndex: 1,
    };
  });
}

export function getLayoutedElements(nodes: Node[], edges: Edge[]) {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 170, ranksep: 85 });
  edges.forEach((e) => g.setEdge(e.source, e.target));
  nodes.forEach((n) =>
    g.setNode(n.id, { width: n.measured?.width ?? NODE_W, height: n.measured?.height ?? NODE_H }),
  );
  Dagre.layout(g);
  const layoutedNodes = nodes.map((n) => {
    const pos = g.node(n.id);
    const w = n.measured?.width ?? NODE_W;
    const h = n.measured?.height ?? NODE_H;
    return {
      ...n,
      position: { x: pos.x - w / 2, y: pos.y - h / 2 },
    };
  });

  return {
    nodes: layoutedNodes,
    edges: routeEdgesAroundNodes(layoutedNodes, edges),
  };
}
